// Store module for Experience creation/editing
// Shares data between Step2, Step4, and other steps
import i18n from '../../i18n'
import { formatDuration } from '../../utils/duration'

// ============================================================
// 🎯 DETECTION KEYS vs OUTPUT KEYS
// ============================================================
// DETECTION_KEYS: Fields the USER actively chooses (inputs to circuit detection)
//   - reservation_mode (Step 3)
//   - multi_space_group_mix, client_space_choice, min_players_to_play, mixing_limit (Step 3 switches)
//
// SPECIAL NULL HANDLING:
//   - mixing_limit: null → treated as 0 (paintball requires 1 explicitly)
//     This ensures paintball is only detected when mixing_limit is explicitly ON.
//   - multi_space_group_mix: null → skipped (not forced to 0)
//     This allows VR to match even without multi_space_group_mix enabled.
//     VR is differentiated from other types by price_per_person in Step 5.
//
// OUTPUT/IGNORED KEYS (excluded from detection):
//   - exact_time (derived from reservation_mode, never user-set)
//   - same_space_group_mix (always 1 except simpleTicket/free_access_all_day → auto-set from reservation_mode)
//
// NOTE: price_per_person & price_per_group ARE detection keys because they
// differentiate paintball (price_per_person=1) from booking (price_per_group=1)
// when all other Step 3 fields are identical. EXCEPTION bowling : son empreinte
// ne porte PAS ces clés (mode tarifaire libre — personne ou groupe), le scoring
// ignore donc ses sélections tarifaires (voir getMatchingCircuits).
// ============================================================
const DETECTION_KEYS = [
    'reservation_mode',
    'multi_space_group_mix',
    'client_space_choice',
    'min_players_to_play',
    'mixing_limit',
    'price_per_person',
    'price_per_group',
    'dynamic_duration_enabled',
]

/**
 * Clés de détection pour lesquelles « l'utilisateur n'a pas touché l'interrupteur » (null) vaut 0,
 * et NON « joker ».
 *
 * - mixing_limit : seul paintball l'exige (config 1). Sans coercition, paintball matcherait toute
 *   empreinte qui ne l'a pas explicitement activé.
 * - dynamic_duration_enabled : c'est LA clé qui déclare un bowling. Sans coercition, un `null`
 *   redeviendrait un joker et `bowling` matcherait toute empreinte à créneaux — exactement
 *   l'ambiguïté qu'on cherche à supprimer.
 *
 * `client_space_choice` en est volontairement ABSENT : c'est un champ existant dont le `null` porte
 * du sens (« l'utilisateur n'a pas décidé »). L'avoir coercé avait imposé de réécrire 63 lignes en
 * base pour ne pas déclasser les paintball/booking existants. Une clé NEUVE n'a pas ce problème.
 *
 * Les autres clés gardent le comportement « joker » : un champ non décidé n'exclut aucun circuit.
 */
const NULL_MEANS_ZERO = ['mixing_limit', 'dynamic_duration_enabled']

const coerceDetectionValue = (key, value) =>
    (value === null || value === undefined) && NULL_MEANS_ZERO.includes(key) ? 0 : value

// Maps Vuex store prestationTypes keys to DB subcategory slugs
// The store uses 'event' but the DB uses 'events'
const DETECTED_TYPE_TO_SLUG = {
    vr: 'vr',
    paintball: 'paintball',
    bowling: 'bowling',
    booking: 'booking',
    event: 'events',
    simpleTicket: 'simpleTicket',
}
const state = () => ({
    // Reservation mode selected in Step2
    selectedReservationMode: null,

    // Mixing configuration
    multi_space_group_mix: false,
    mixing_authorize_control: false,

    // Current detected circuit type
    currentCircuit: null,

    // ✅ Control visibility of detected type block
    showDetectedType: false,

    // Installations (Spaces)
    installations: [],

    dynamicDurationBlocks: [],

    selected_interval_reservation: null,

    // Step 6: Additional sales
    additional_sales: [],
    display_facilities_names: false,

    // User selections for circuit detection
    userSelections: {
        price_per_person: null,
        price_per_group: null,
        multi_space_group_mix: null,
        client_space_choice: null,
        same_space_group_mix: null,
        min_players_to_play: null,
        exact_time: null,
        mixing_limit: null,
        reservation_mode: null,
        dynamic_duration_enabled: null,
    },
    // Tickets Possibles state
    min_ticket: null,
    max_ticket: null,
    list_ticket_possibles: [],
    is_ticket_possibles: true,
    // Other shared data can be added here as needed
    experienceData: {},
    price: '',
    data_accompte: {
        isAccompte: false,
        acompte: {
            type: null,
            value: null,
        },
    },

    // Prestation type configurations
    // Mirrors the PHP PrestationTypeConfigs constants
    // These configurations define the behavior and constraints for each prestation type
    prestationTypes: {
        vr: {
            price_per_person: 1, // Pricing mode: Tarif par personne (Step 5)
            price_per_group: 0, // Pricing mode: Tarif par groupe (Step 5)
            multi_space_group_mix: 1, // Mixer les groupes entre plusieurs espaces (Step 3)
            client_space_choice: 0, // Choix de l'espace par le client (Step 3)
            same_space_group_mix: 1, // Mixer les groupes sur le même espace (Step 3)
            min_players_to_play: 0, // Minimum de places requis pour jouer (Step 3)
            exact_time: 1, // Réservation avec heure exacte (Step 4)
            mixing_limit: 0, // Limiter le mixage à partir d'un nombre (Step 3)
            reservation_mode: 'multiple_time_slots', // Mode de réservation (Step 2/4)
            dynamic_duration_enabled: 0, // Durée variable selon le nombre de personnes (Step 3)
        },
        paintball: {
            price_per_person: 1, // Pricing mode: Tarif par personne (Step 5)
            price_per_group: 0, // Pricing mode: Tarif par groupe (Step 5)
            multi_space_group_mix: 0, // Mixer les groupes entre plusieurs espaces (Step 3)
            client_space_choice: 1, // Choix de l'espace par le client (Step 3)
            same_space_group_mix: 1, // Mixer les groupes sur le même espace (Step 3)
            min_players_to_play: 1, // Minimum de places requis pour jouer (Step 3)
            exact_time: 1, // Réservation avec heure exacte (Step 4)
            mixing_limit: 1, // Limiter le mixage à partir d'un nombre (Step 3)
            reservation_mode: 'multiple_time_slots', // Mode de réservation (Step 2/4)
            dynamic_duration_enabled: 0, // Durée variable selon le nombre de personnes (Step 3)
        },
        // Bowling : mêmes mécaniques serveur que `booking` (créneaux, disponibilités), facturé à la
        // personne PAR DÉFAUT. dynamic_duration_enabled est LA clé qui le sépare de `booking`
        // (identique par ailleurs) et de `vr` ; mixing_limit=0 le distingue de `paintball`.
        //
        // Miroir de PrestationTypeConfigs (PHP) : les clés tarifaires sont VOLONTAIREMENT ABSENTES
        // — un bowling peut être facturé à la personne OU au groupe (forfait tout compris), le
        // choix du gérant n'influence ni la détection ni le verrou de l'étape Tarification.
        bowling: {
            multi_space_group_mix: 0, // Mixer les groupes entre plusieurs espaces (Step 3)
            client_space_choice: 1, // Choix de l'espace par le client (Step 3)
            same_space_group_mix: 1, // Mixer les groupes sur le même espace (Step 3)
            min_players_to_play: 1, // Minimum de places requis pour jouer (Step 3)
            exact_time: 1, // Réservation avec heure exacte (Step 4)
            mixing_limit: 0, // Limiter le mixage à partir d'un nombre (Step 3)
            reservation_mode: 'multiple_time_slots', // Mode de réservation (Step 2/4)
            // LA clé qui déclare un bowling : elle le sépare de `booking` (identique par ailleurs)
            // et de `vr`, sans dépendre du choix d'espace NI du mode tarifaire.
            dynamic_duration_enabled: 1, // Durée variable selon le nombre de personnes (Step 3)
        },
        booking: {
            price_per_person: 0, // Pricing mode: Tarif par personne (Step 5)
            price_per_group: 1, // Pricing mode: Tarif par groupe (Step 5)
            multi_space_group_mix: 0, // Mixer les groupes entre plusieurs espaces (Step 3)
            client_space_choice: 1, // Choix de l'espace par le client (Step 3)
            same_space_group_mix: 1, // Mixer les groupes sur le même espace (Step 3)
            min_players_to_play: 1, // Minimum de places requis pour jouer (Step 3)
            exact_time: 1, // Réservation avec heure exacte (Step 4)
            mixing_limit: 0, // Limiter le mixage à partir d'un nombre (Step 3)
            reservation_mode: 'multiple_time_slots', // Mode de réservation (Step 2/4)
            dynamic_duration_enabled: 0, // Durée variable selon le nombre de personnes (Step 3)
        },
        event: {
            price_per_person: 1, // Pricing mode: Tarif par personne (Step 5)
            price_per_group: 0, // Pricing mode: Tarif par groupe (Step 5)
            multi_space_group_mix: 0, // Mixer les groupes entre plusieurs espaces (Step 3)
            client_space_choice: 0, // Choix de l'espace par le client (Step 3)
            same_space_group_mix: 1, // Mixer les groupes sur le même espace (Step 3)
            min_players_to_play: 1, // Minimum de places requis pour jouer (Step 3)
            exact_time: 1, // Réservation avec heure exacte (Step 4)
            mixing_limit: 0, // Limiter le mixage à partir d'un nombre (Step 3)
            reservation_mode: 'one_schedule_for_everyone', // Mode de réservation (Step 2/4)
            dynamic_duration_enabled: 0, // Durée variable selon le nombre de personnes (Step 3)
        },
        simpleTicket: {
            price_per_person: 1, // Pricing mode: Tarif par personne (Step 5)
            price_per_group: 0, // Pricing mode: Tarif par groupe (Step 5)
            multi_space_group_mix: 0, // Mixer les groupes entre plusieurs espaces (Step 3)
            client_space_choice: 0, // Choix de l'espace par le client (Step 3)
            same_space_group_mix: 0, // Mixer les groupes sur le même espace (Step 3)
            min_players_to_play: 0, // Minimum de places requis pour jouer (Step 3)
            exact_time: 0, // Réservation avec heure exacte (Step 4)
            mixing_limit: 0, // Limiter le mixage à partir d'un nombre (Step 3)
            reservation_mode: 'free_access_all_day', // Mode de réservation (Step 2/4)
            dynamic_duration_enabled: 0, // Durée variable selon le nombre de personnes (Step 3)
        },
    },
})

const mutations = {
    SET_RESERVATION_MODE(state, mode) {
        console.log('🔵 Store - Setting reservation mode:', mode)
        state.selectedReservationMode = mode
        state.userSelections.reservation_mode = mode

        // Auto-set same_space_group_mix: always 1 except free_access_all_day (simpleTicket)
        const sameSpaceValue = mode === 'free_access_all_day' ? 0 : 1
        state.userSelections.same_space_group_mix = sameSpaceValue
        console.log('🔵 Store - Auto-set same_space_group_mix:', sameSpaceValue, '(from reservation_mode:', mode, ')')

        // free_access_all_day → simpleTicket: force multi_space_group_mix OFF
        // simpleTicket requires multi_space_group_mix=0, so reset it to ensure detection works
        if (mode === 'free_access_all_day') {
            state.multi_space_group_mix = false
            state.userSelections.multi_space_group_mix = null
            console.log('🔵 Store - Auto-reset multi_space_group_mix to null (free_access_all_day → simpleTicket)')
        }
    },

    CLEAR_RESERVATION_MODE(state) {
        console.log('🔵 Store - Clearing reservation mode')
        state.selectedReservationMode = null
    },

    SET_multi_space_group_mix(state, value) {
        console.log('🔵 Store - Setting multi_space_group_mix:', value)
        state.multi_space_group_mix = value
        // Use null for OFF (undecided), 1 for ON (explicit choice)
        // Never use 0 — it means "user explicitly chose OFF" and filters in matching
        state.userSelections.multi_space_group_mix = value ? 1 : null
    },

    SET_MIXING_AUTHORIZE_CONTROL(state, value) {
        console.log('🔵 Store - Setting mixing_authorize_control:', value)
        state.mixing_authorize_control = value
    },

    SET_USER_SELECTION(state, { key, value }) {
        console.log(`🔵 Store - Setting user selection ${key}:`, value)
        state.userSelections[key] = value
    },

    SET_CURRENT_CIRCUIT(state, circuit) {
        console.log('🔵 Store - Setting current circuit:', circuit)
        state.currentCircuit = circuit
    },

    SET_SHOW_DETECTED_TYPE(state, value) {
        console.log('🔵 Store - Setting showDetectedType:', value)
        state.showDetectedType = value
    },

    APPLY_CIRCUIT_CONFIG(state, circuitType) {
        console.log('🔵 Store - Circuit detected:', circuitType)
        // Don't automatically apply all values
        // Just set the current circuit for reference
        state.currentCircuit = circuitType

        // Optional: Only apply values that are not yet set (null)
        const config = state.prestationTypes[circuitType]
        if (config) {
            Object.keys(config).forEach((key) => {
                // Only set if user hasn't made a choice yet
                if (state.userSelections[key] === null) {
                    // Don't auto-fill, let user choose
                    // state.userSelections[key] = config[key]
                }
            })
        }
    },

    SET_EXPERIENCE_DATA(state, data) {
        state.experienceData = { ...state.experienceData, ...data }
    },

    SET_DYNAMIC_DURATION_BLOCKS(state, blocks) {
        state.dynamicDurationBlocks = blocks
    },

    ADD_DYNAMIC_DURATION_BLOCK(state, block) {
        state.dynamicDurationBlocks.push(block)
    },

    REMOVE_DYNAMIC_DURATION_BLOCK(state, index) {
        state.dynamicDurationBlocks.splice(index, 1)
    },

    UPDATE_DYNAMIC_DURATION_BLOCK(state, { index, block }) {
        state.dynamicDurationBlocks.splice(index, 1, block)
    },

    CLEAR_DYNAMIC_DURATION_BLOCKS(state) {
        state.dynamicDurationBlocks = []
    },

    SET_SELECTED_INTERVAL_RESERVATION(state, interval) {
        console.log('🔵 Store - Setting selected interval reservation:', interval)
        state.selected_interval_reservation = interval
    },

    CLEAR_EXPERIENCE_DATA(state) {
        state.selectedReservationMode = null
        state.multi_space_group_mix = false
        state.mixing_authorize_control = false
        state.currentCircuit = null
        state.userSelections = {
            price_per_person: null,
            price_per_group: null,
            multi_space_group_mix: null,
            client_space_choice: null,
            same_space_group_mix: null,
            min_players_to_play: null,
            exact_time: null,
            mixing_limit: null,
            reservation_mode: null,
            dynamic_duration_enabled: null,
        }
        state.experienceData = {}
        state.dynamicDurationBlocks = []
        state.selected_interval_reservation = null
        state.min_ticket = null
        state.max_ticket = null
    },

    SET_INSTALLATIONS(state, installations) {
        console.log('🔵 Store - Setting installations:', installations?.length || 0)
        state.installations = installations || []
    },
    SET_PRICE(state, price) {
        state.price = price
    },
    SET_ADDITIONAL_SALES(state, sales) {
        console.log('🔵 Store - Setting additional sales:', sales?.length || 0)
        state.additional_sales = sales || []
    },
    ADD_ADDITIONAL_SALE(state, sale) {
        state.additional_sales.push(sale)
    },
    UPDATE_ADDITIONAL_SALE(state, { index, sale }) {
        state.additional_sales.splice(index, 1, sale)
    },
    SET_DISPLAY_FACILITIES_NAMES(state, value) {
        state.display_facilities_names = value
    },
    SET_MIN_TICKET(state, value) {
        state.min_ticket = value
    },
    SET_MAX_TICKET(state, value) {
        state.max_ticket = value
    },
    SET_LIST_TICKET_POSSIBLES(state, value) {
        state.list_ticket_possibles = value
    },
    SET_IS_TICKET_POSSIBLES(state, value) {
        state.is_ticket_possibles = value
    },
    SET_DATA_ACCOMPTE(state, value) {
        state.data_accompte = value
    },
}

const actions = {
    setReservationMode({ commit }, mode) {
        commit('SET_RESERVATION_MODE', mode)
    },

    clearReservationMode({ commit }) {
        commit('CLEAR_RESERVATION_MODE')
    },

    setAuthoriseMixed({ commit, dispatch }, value) {
        commit('SET_multi_space_group_mix', value)
        // Auto-detect circuit after change
        dispatch('detectAndApplyCircuit')
    },

    setMixingAuthorizeControl({ commit }, value) {
        commit('SET_MIXING_AUTHORIZE_CONTROL', value)
    },

    setUserSelection({ commit, dispatch }, { key, value }) {
        commit('SET_USER_SELECTION', { key, value })
        // Auto-detect circuit after change
        dispatch('detectAndApplyCircuit')
    },

    applyCircuitConfig({ commit }, circuitType) {
        commit('APPLY_CIRCUIT_CONFIG', circuitType)
    },

    // ✅ Toggle visibility of detected type block
    setShowDetectedType({ commit }, value) {
        commit('SET_SHOW_DETECTED_TYPE', value)
    },

    detectAndApplyCircuit({ state, commit, getters }) {
        const matchingCircuits = getters.getMatchingCircuits
        console.log('🔍 Matching circuits:', matchingCircuits)

        if (matchingCircuits.length === 1) {
            // Exact match found - apply circuit config
            commit('APPLY_CIRCUIT_CONFIG', matchingCircuits[0])
        } else if (matchingCircuits.length > 1) {
            // Multiple matches - set current circuit to null (ambiguous)
            commit('SET_CURRENT_CIRCUIT', null)
        } else {
            // No match
            commit('SET_CURRENT_CIRCUIT', null)
        }
    },

    setExperienceData({ commit }, data) {
        commit('SET_EXPERIENCE_DATA', data)
    },

    setDynamicDurationBlocks({ commit }, blocks) {
        commit('SET_DYNAMIC_DURATION_BLOCKS', blocks)
    },

    addDynamicDurationBlock({ commit }, block) {
        commit('ADD_DYNAMIC_DURATION_BLOCK', block)
    },

    removeDynamicDurationBlock({ commit }, index) {
        commit('REMOVE_DYNAMIC_DURATION_BLOCK', index)
    },

    updateDynamicDurationBlock({ commit }, { index, block }) {
        commit('UPDATE_DYNAMIC_DURATION_BLOCK', { index, block })
    },

    clearDynamicDurationBlocks({ commit }) {
        commit('CLEAR_DYNAMIC_DURATION_BLOCKS')
    },

    setSelectedIntervalReservation({ commit }, interval) {
        commit('SET_SELECTED_INTERVAL_RESERVATION', interval)
    },

    clearExperienceData({ commit }) {
        commit('CLEAR_EXPERIENCE_DATA')
    },

    setInstallations({ commit }, installations) {
        commit('SET_INSTALLATIONS', installations)
    },

    setPrice({ commit }, price) {
        commit('SET_PRICE', price)
    },
    setAdditionalSales({ commit }, sales) {
        commit('SET_ADDITIONAL_SALES', sales)
    },
    addAdditionalSale({ commit }, sale) {
        commit('ADD_ADDITIONAL_SALE', sale)
    },
    updateAdditionalSale({ commit }, payload) {
        commit('UPDATE_ADDITIONAL_SALE', payload)
    },
    setDisplayFacilitiesNames({ commit }, value) {
        commit('SET_DISPLAY_FACILITIES_NAMES', value)
    },
    setMinTicket({ commit }, value) {
        commit('SET_MIN_TICKET', value)
    },
    setMaxTicket({ commit }, value) {
        commit('SET_MAX_TICKET', value)
    },
    setListTicketPossibles({ commit }, value) {
        commit('SET_LIST_TICKET_POSSIBLES', value)
    },
    setIsTicketPossibles({ commit }, value) {
        commit('SET_IS_TICKET_POSSIBLES', value)
    },
    setDataAccompte({ commit }, value) {
        commit('SET_DATA_ACCOMPTE', value)
    },
}

const getters = {
    selectedReservationMode: (state) => state.selectedReservationMode,
    reservationMode: (state) => state.selectedReservationMode,
    authoriseMixed: (state) => state.multi_space_group_mix,
    mixingAuthorizeControl: (state) => state.mixing_authorize_control,
    experienceData: (state) => state.experienceData,
    currentCircuit: (state) => state.currentCircuit,
    showDetectedType: (state) => state.showDetectedType, // ✅ Visibility of detected type block
    userSelections: (state) => state.userSelections,
    dynamicDurationBlocks: (state) => state.dynamicDurationBlocks,
    selected_interval_reservation: (state) => state.selected_interval_reservation,
    installations: (state) => state.installations,
    visibleInstallations: (state) => state.installations.filter((i) => !i.deleted),
    selectedInstallations: (state) => state.installations.filter((i) => i.selected && !i.deleted),

    // Get filtered duration options based on interval and already selected values
    // Comment une durée de palier stockée doit se présenter dans le formulaire.
    //
    // Trois cas, et le troisième est celui qu'on oublie :
    //   - absente (0/null)              -> champ vide, à renseigner ;
    //   - présente dans la liste         -> la valeur telle quelle ;
    //   - HORS grille (ex. 195 min)      -> « Personnalisée » (-1) + la valeur dans `personilize`.
    // Sans le troisième cas, une durée hors grille s'afficherait vide et un simple enregistrement
    // la perdrait silencieusement.
    getTierDurationFields: (state, getters) => (lot) => {
        const stored = Number(lot?.duration)

        if (!stored) {
            return { duration: '', personilize: null }
        }

        const isSelectable = getters.getTierDurationOptions().some((option) => Number(option.value) === stored)

        return isSelectable ? { duration: stored, personilize: null } : { duration: -1, personilize: stored }
    },

    // Durées proposées pour un PALIER (nb de personnes -> durée) — et, depuis que deux options
    // peuvent partager une durée, pour les OPTIONS aussi (getFilteredDurations y délègue) : un
    // palier peut valoir la durée de son option ou celle d'une autre, aucune n'est retirée.
    //
    // Pas d'entrée « vide » : sur une expérience à durée dynamique la durée est OBLIGATOIRE
    // (critère du ticket). Un choix sélectionnable mais invalide serait un piège — pour retirer
    // une surcharge, on supprime le palier.
    getTierDurationOptions: (state) => () => {
        const interval = Number(state.selected_interval_reservation)

        if (!interval || interval <= 0) {
            return []
        }

        const options = []

        const maxDuration = 180
        for (let duration = interval; duration <= maxDuration; duration += interval) {
            options.push({
                label: formatDuration(duration, i18n),
                value: duration,
            })
        }

        // « Personnalisée » : même convention que les options de durée — la valeur -1 est un
        // marqueur, la vraie durée vit dans `personilize`. Sans elle, la liste ne peut exprimer
        // que les multiples de l'intervalle, ce qui rend inaccessibles des paliers comme 20, 28
        // ou 36 min — précisément l'exemple du ticket.
        options.push({
            label: i18n.t('experience.step4.custom_duration.title'),
            value: -1,
        })

        return options
    },

    // Durées proposées pour une OPTION de durée : la grille complète + « Personnalisée ».
    //
    // Aucune durée n'est retirée : deux options peuvent partager la même durée (des formules
    // différentes à 120 min), une option est identifiée par son id, jamais par ses minutes.
    // La liste est donc celle des paliers ; le nom et les paramètres (valeur courante, durée
    // principale) sont conservés pour les appelants existants, mais n'excluent plus rien.
    // eslint-disable-next-line no-unused-vars
    getFilteredDurations:
        (state, getters) =>
        (excludeCurrentValue = null, selected_duration_experience = null) =>
            getters.getTierDurationOptions(),

    // Get configuration for a specific prestation type
    getPrestationTypeConfig: (state) => (type) => {
        return state.prestationTypes[type] || null
    },

    // Get all prestation types
    allPrestationTypes: (state) => state.prestationTypes,

    // Check if a specific criterion is enabled for a type
    isTypeConfigEnabled: (state) => (type, criterion) => {
        return state.prestationTypes[type]?.[criterion] === 1
    },

    // Get matching circuits based on current user selections
    // Only compares DETECTION_KEYS (user inputs from Step 3 + Step 5 pricing).
    // exact_time, same_space_group_mix are excluded (see DETECTION_KEYS comment above).
    getMatchingCircuits: (state) => {
        const matching = []

        // Hard filter: reservation_mode is a primary discriminator.
        // If set, immediately exclude any type whose reservation_mode doesn't match.
        const userReservationMode = state.userSelections.reservation_mode

        // Debug: log current DETECTION_KEY values in store
        const debugSelections = {}
        DETECTION_KEYS.forEach((key) => {
            debugSelections[key] = state.userSelections[key]
        })
        console.log('🔎 getMatchingCircuits - Current DETECTION_KEY values:', JSON.stringify(debugSelections))

        Object.keys(state.prestationTypes).forEach((circuitType) => {
            const config = state.prestationTypes[circuitType]

            // Hard filter: reservation_mode must match if set
            if (
                userReservationMode !== null &&
                userReservationMode !== undefined &&
                config.reservation_mode !== userReservationMode
            ) {
                console.log(
                    `  ❌ ${circuitType} excluded by reservation_mode: user=${userReservationMode} vs config=${config.reservation_mode}`
                )
                return
            }

            let isMatch = true
            let excludedBy = null

            // Only compare DETECTION_KEYS (user-input fields)
            DETECTION_KEYS.forEach((key) => {
                let userValue = state.userSelections[key]
                const configValue = config[key]

                // `null` n'est pas un joker pour ces deux clés : voir coerceDetectionValue.
                userValue = coerceDetectionValue(key, userValue)

                // Skip null/undefined (user hasn't made a choice yet for this field)
                if (userValue === null || userValue === undefined) {
                    return
                }

                // Clé ABSENTE de l'empreinte du circuit (bowling : mode tarifaire libre) — le
                // circuit ne se prononce pas sur ce champ, il ne peut donc pas en être exclu.
                // Miroir du scoring PHP (CircuitDetectionService::calculateTypeScore), qui itère
                // sur les clés de la CONFIG et non sur les clés de détection.
                if (configValue === undefined) {
                    return
                }

                // Normalize booleans to integers for comparison
                const normalizedUser = typeof userValue === 'boolean' ? (userValue ? 1 : 0) : userValue
                const normalizedConfig = typeof configValue === 'boolean' ? (configValue ? 1 : 0) : configValue

                if (normalizedUser != normalizedConfig) {
                    isMatch = false
                    if (!excludedBy) excludedBy = `${key}: user=${normalizedUser} vs config=${normalizedConfig}`
                }
            })

            if (isMatch) {
                matching.push(circuitType)
            } else {
                console.log(`  ❌ ${circuitType} excluded by: ${excludedBy}`)
            }
        })

        return matching
    },

    // Check if Step 3 fields alone (without pricing) narrow to exactly 1 circuit.
    // Used by Step 5 to decide if pricing should be auto-set and locked.
    getMatchingCircuitsWithoutPricing: (state) => {
        const step3Keys = DETECTION_KEYS.filter((k) => k !== 'price_per_person' && k !== 'price_per_group')
        const matching = []

        // Hard filter: reservation_mode must match if set
        const userReservationMode = state.userSelections.reservation_mode

        Object.keys(state.prestationTypes).forEach((circuitType) => {
            const config = state.prestationTypes[circuitType]

            // Hard filter: reservation_mode must match if set
            if (
                userReservationMode !== null &&
                userReservationMode !== undefined &&
                config.reservation_mode !== userReservationMode
            ) {
                return
            }

            let isMatch = true

            step3Keys.forEach((key) => {
                let userValue = state.userSelections[key]
                const configValue = config[key]

                // `null` n'est pas un joker pour ces deux clés : voir coerceDetectionValue.
                userValue = coerceDetectionValue(key, userValue)

                if (userValue === null || userValue === undefined) return

                const normalizedUser = typeof userValue === 'boolean' ? (userValue ? 1 : 0) : userValue
                const normalizedConfig = typeof configValue === 'boolean' ? (configValue ? 1 : 0) : configValue

                if (normalizedUser != normalizedConfig) {
                    isMatch = false
                }
            })

            if (isMatch) matching.push(circuitType)
        })

        return matching
    },

    // Check if a field should be disabled based on current circuit
    isFieldDisabled: (state) => (fieldName) => {
        if (!state.currentCircuit) return false

        const config = state.prestationTypes[state.currentCircuit]
        if (!config) return false

        // Field is disabled if it's defined in the circuit config
        return config.hasOwnProperty(fieldName)
    },

    // Get the value a field should have based on current circuit
    getFieldValue: (state) => (fieldName) => {
        if (!state.currentCircuit) return null

        const config = state.prestationTypes[state.currentCircuit]
        return config?.[fieldName] ?? null
    },
    price: (state) => state.price, // <-- getter for price
    additional_sales: (state) => state.additional_sales,
    display_facilities_names: (state) => state.display_facilities_names,
    min_ticket: (state) => state.min_ticket,
    max_ticket: (state) => state.max_ticket,
    list_ticket_possibles: (state) => state.list_ticket_possibles,
    is_ticket_possibles: (state) => state.is_ticket_possibles,
    data_accompte: (state) => state.data_accompte,

    // ============================================================
    // 🔥 NOUVEAUX GETTERS POUR DÉTECTION AMÉLIORÉE
    // ============================================================

    /**
     * Calcule le score de correspondance pour chaque type de prestation
     * Retourne un objet { vr: 0.8, paintball: 1.0, booking: 0.6, ... }
     *
     * Score = (nombre de champs qui correspondent) / (nombre total de champs définis)
     *
     * @returns {Object} Scores par type
     */
    getDetectionScores: (state) => {
        const scores = {}

        // Hard filter: reservation_mode must match if set
        const userReservationMode = state.userSelections.reservation_mode

        Object.keys(state.prestationTypes).forEach((circuitType) => {
            const config = state.prestationTypes[circuitType]

            // Hard filter: reservation_mode must match if set — score = 0
            if (
                userReservationMode !== null &&
                userReservationMode !== undefined &&
                config.reservation_mode !== userReservationMode
            ) {
                scores[circuitType] = 0
                return
            }

            let total = 0
            let matches = 0

            // Only compare DETECTION_KEYS (user-input fields, not output fields)
            DETECTION_KEYS.forEach((key) => {
                let userValue = state.userSelections[key]
                const configValue = config[key]

                // `null` n'est pas un joker pour ces deux clés : voir coerceDetectionValue.
                userValue = coerceDetectionValue(key, userValue)

                // Ignore les valeurs null/undefined (pas encore choisies par l'utilisateur)
                if (userValue === null || userValue === undefined) {
                    return
                }

                total++

                // Conversion pour comparaison (bool → int, string → valeur)
                const normalizedUserValue = typeof userValue === 'boolean' ? (userValue ? 1 : 0) : userValue
                const normalizedConfigValue = typeof configValue === 'boolean' ? (configValue ? 1 : 0) : configValue

                if (normalizedUserValue == normalizedConfigValue) {
                    matches++
                }
            })

            // Score = pourcentage de correspondance
            scores[circuitType] = total > 0 ? matches / total : 0
        })

        return scores
    },

    /**
     * Retourne le meilleur type détecté avec son score et niveau de confiance
     *
     * @returns {Object} {
     *   type: 'paintball',
     *   score: 1.0,
     *   confidence: 'exact',
     *   allScores: { vr: 0.6, paintball: 1.0, ... }
     * }
     */
    getBestDetectedType: (state, getters) => {
        const scores = getters.getDetectionScores

        // Trouve le type avec le meilleur score
        let bestType = null
        let bestScore = 0

        Object.entries(scores).forEach(([type, score]) => {
            if (score > bestScore) {
                bestScore = score
                bestType = type
            }
        })

        // Détermine le niveau de confiance
        let confidence = 'none'
        if (bestScore >= 1.0) {
            confidence = 'exact' // 100% match parfait
        } else if (bestScore >= 0.8) {
            confidence = 'high' // 80%+ très probable
        } else if (bestScore >= 0.6) {
            confidence = 'medium' // 60%+ possible
        } else if (bestScore >= 0.4) {
            confidence = 'low' // 40%+ peu probable
        }

        return {
            type: bestType,
            score: bestScore,
            confidence: confidence,
            allScores: scores,
        }
    },

    /**
     * Vérifie si les sélections actuelles correspondent EXACTEMENT à un type (100%)
     *
     * @returns {boolean}
     */
    hasExactMatch: (state, getters) => {
        const detection = getters.getBestDetectedType
        return detection.score === 1.0
    },

    /**
     * Retourne tous les types qui dépassent un seuil de score donné
     * Utile pour détecter les ambiguïtés (plusieurs types possibles)
     *
     * @param {number} threshold - Seuil minimum (défaut: 0.6)
     * @returns {Array} [{ type: 'vr', score: 0.8 }, { type: 'paintball', score: 0.9 }]
     */
    getPossibleTypes:
        (state, getters) =>
        (threshold = 0.6) => {
            const scores = getters.getDetectionScores

            return Object.entries(scores)
                .filter(([type, score]) => score >= threshold)
                .map(([type, score]) => ({ type, score }))
                .sort((a, b) => b.score - a.score) // Trier par score descendant
        },

    /**
     * Valide que les userSelections correspondent bien à un type donné
     * Utilisé pour vérifier la cohérence avant sauvegarde
     *
     * @param {string} targetType - Type à valider (ex: 'paintball')
     * @returns {Object} { valid: true/false, errors: [...] }
     */
    validateAgainstType: (state) => (targetType) => {
        const config = state.prestationTypes[targetType]

        if (!config) {
            return {
                valid: false,
                errors: [
                    {
                        field: 'type',
                        message: `Type "${targetType}" n'existe pas dans les configurations`,
                    },
                ],
            }
        }

        const errors = []

        Object.entries(config).forEach(([key, expectedValue]) => {
            const actualValue = state.userSelections[key]

            // Ignore si pas encore défini par l'utilisateur
            if (actualValue === null || actualValue === undefined) {
                return
            }

            // Normalisation pour comparaison
            const normalizedActual = typeof actualValue === 'boolean' ? (actualValue ? 1 : 0) : actualValue
            const normalizedExpected = typeof expectedValue === 'boolean' ? (expectedValue ? 1 : 0) : expectedValue

            if (normalizedActual != normalizedExpected) {
                errors.push({
                    field: key,
                    expected: expectedValue,
                    actual: actualValue,
                    message: `Le champ "${key}" devrait être ${expectedValue} pour le type "${targetType}", mais est ${actualValue}`,
                })
            }
        })

        return {
            valid: errors.length === 0,
            errors: errors,
        }
    },

    /**
     * Détecte s'il y a une ambiguïté (plusieurs types possibles)
     *
     * @returns {boolean}
     */
    hasAmbiguity: (state, getters) => {
        const possible = getters.getPossibleTypes(0.8)
        return possible.length > 1
    },

    /**
     * Retourne un message lisible décrivant la détection actuelle
     *
     * @returns {string}
     */
    getDetectionMessage: (state, getters) => {
        const detection = getters.getBestDetectedType

        if (!detection.type) {
            return 'Aucun type détecté - continuez à configurer votre expérience'
        }

        const typeLabels = {
            vr: 'Réalité Virtuelle (VR)',
            paintball: 'Paintball',
            bowling: 'Bowling',
            booking: 'Réservation',
            event: 'Événement',
            simpleTicket: 'Billet Simple',
        }

        const typeName = typeLabels[detection.type] || detection.type
        const percentage = Math.round(detection.score * 100)

        switch (detection.confidence) {
            case 'exact':
                return `✓ Type détecté : ${typeName} (100% - Exact)`
            case 'high':
                return `⚡ Type probable : ${typeName} (${percentage}%)`
            case 'medium':
                return `⚠️ Type possible : ${typeName} (${percentage}%)`
            case 'low':
                return `❓ Type incertain : ${typeName} (${percentage}%)`
            default:
                return `Configuration en cours...`
        }
    },

    /**
     * Returns the primary detected type based on EXACT matching (not scoring).
     * Uses getMatchingCircuits with reservation_mode hard filter.
     * Returns the DB slug (e.g. 'events' not 'event') for backend compatibility.
     * Returns null if 0 or >1 circuits match (ambiguous or no match).
     */
    getPrimaryDetectedType: (state, getters) => {
        const matching = getters.getMatchingCircuits

        if (matching.length !== 1) {
            return null
        }

        const detectedType = matching[0]
        return DETECTED_TYPE_TO_SLUG[detectedType] || detectedType
    },
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
}
